<template>
  <div
    class="UIPageEmptyState"
    :style="{ backgroundImage: `url(${pageBackground})` }"
  >
    <UIEmptyState
      class="UIPageEmptyState__content"
      :icon="icon"
      size="lg"
      titleTag="h1"
      :title="title"
      :description="description"
    >
      <template v-if="hint" #description>
        {{ description }}
        <span class="UIPageEmptyState__hint">{{ hint }}</span>
      </template>

      <!-- Optional call to action — plenty of these states are terminal
           (an unpublished page, a maintenance window nobody can shortcut). -->
      <template v-if="$slots.actions" #actions>
        <slot name="actions" />
      </template>
    </UIEmptyState>
  </div>
</template>

<script lang="ts" setup>
import { UIEmptyState } from '@foundever/ui-lib'

import pageBackground from './assets/full-page-background.jpg'

import type { IIcons } from '@foundever/ui-lib'

/**
 * A whole-viewport empty state: full-bleed artwork with a centred `UIEmptyState`.
 *
 * The shell for any page that is nothing but a message — a dead share link, a GPT that
 * was removed, a maintenance window. Carries no copy of its own; every caller states
 * what it is telling the visitor.
 */
defineProps<{
  icon?: IIcons
  title?: string
  description?: string
  /** Secondary line under the description — usually what the visitor can do next. */
  hint?: string
}>()
</script>

<style scoped lang="scss">
.UIPageEmptyState {
  box-sizing: border-box;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(---fds-global-space-xl);
  // Full-bleed artwork; `--bg-primary` is the fallback color under it (shown while
  // the image loads or if it fails). The image URL is set inline from the imported
  // asset (Vite fingerprints it), so only sizing lives here.
  background-color: var(--bg-primary);
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;

  .UIPageEmptyState__content {
    width: 100%;
    :deep(.UIEmptyState__content) {
      max-width: 100%;
    }
  }

  .UIPageEmptyState__hint {
    display: block;
    margin-top: var(---fds-global-space-md);
    color: var(--text-tertiary);
  }
}
</style>
